import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { checkArray, Page_Size } from '../../../Components/helper';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import * as action from '../store/Document.action';
import * as systemMasterAction from '../../SystemMasters/Store/SystemMasters.action';
import * as bomLibAction from '../../Library/BomLibrary/store/BomLibrary.action';

export const StateContext = createContext(null);

export default function ManualDocStateProvider({ children }) {
  const reducerState = useSelector((redState) => redState);
  const {
    UserDetails: { userInfo, isSupplierLoggedIn },
    Users: { logInRights },
    SystemMastersDetails: { subBrandAccess },
  } = reducerState;
  const { CheckActionPermission } = useComponent();
  const dispatch = useDispatch();

  const manualAddEditAccess = CheckActionPermission(logInRights, 'manuals', 'all', 'addEdit');
  const manualDeleteAccess = CheckActionPermission(logInRights, 'manuals', 'all', 'delete');

  const initialSetting = {
    documentList: [],
    loading: false,
    hasMore: false,
    pageIndex: 1,
    pageSize: Page_Size,
    selectedOption: null,
    searchText: '',
    searchLoaded: false,
    deleteId: null,
    showConfirmationPopup: false,
    selectedIds: [],
    isArchived: false,
    archivedId: null,
    archiveComment: '',
    showArchivePopup: false,
    selectedFilterType: [],
    selectedFilterBrand: [],
    selectedFilterSuppliers: [],
    selectedFilterSubBrand: [],
    isFilter: false,
    selectedExpiryDate: '',
    startDate: null,
    endDate: null,
    isFromRenew: false,
    selectedFolder: null,
    selectedDocumentId: null,
    isFolderPopup: false,
    isOpenShareDocPopup: false,
    isPreview: false,
    breadCrumbData: [],
    isEditFolder: false,
    isEditFile: false,
    isPageLoader: false,
    isSubBrandAccess: false,
  };
  const [state, setState] = useState(initialSetting);
  const isallChecked = checkArray(state?.documentList) && state?.selectedIds?.length === state?.documentList?.length;

  const apiCallWithFilter = useCallback(
    async (localstorageFilter, folderId, fromArchieve) => {
      let reqObj = {};
      if (
        localstorageFilter &&
        (localstorageFilter?.brand?.length ||
          localstorageFilter?.suppliers?.length ||
          localstorageFilter?.documentFilterType ||
          localstorageFilter?.subBrand?.length ||
          localstorageFilter?.startDate ||
          localstorageFilter?.endDate)
      ) {
        reqObj = {
          pageIndex: 1,
          isArchived: fromArchieve ? !state?.isArchived : state?.isArchived,
          pageSize: state?.pageSize,
          documentType: localstorageFilter?.documentFilterType || [],
          brand: localstorageFilter?.brand || [],
          subBrand: localstorageFilter?.subBrand || [],
          suppliers: localstorageFilter?.suppliers || [],
          folderId,
          startDate: localstorageFilter?.startDate || null,
          endDate: localstorageFilter?.endDate || null,
        };

        setState((prevState) => ({
          ...prevState,
          selectedFilterType: localstorageFilter?.documentFilterType || [],
          selectedFilterBrand: localstorageFilter?.brand || [],
          selectedFilterSubBrand: localstorageFilter?.subBrand || [],
          selectedFilterSuppliers: localstorageFilter.suppliers || [],
          startDate: localstorageFilter.startDate || null,
          endDate: localstorageFilter.endDate || null,
          isFilter: true,
          loading: true,
        }));

        const res = await dispatch(action.getManualDocumentList(reqObj));
        if (res) {
          setState((prev) => ({
            ...prev,
            loading: false,
            pageIndex: 1,
          }));
        }
      }
    },
    [dispatch, state?.isArchived, state?.pageSize], // Add necessary dependencies here
  );

  useEffect(() => {
    dispatch(systemMasterAction.getSubBrandAccess());
  }, [dispatch]);

  useEffect(() => {
    if (subBrandAccess !== undefined) {
      setState((prev) => ({
        ...prev,
        isSubBrandAccess: subBrandAccess,
      }));
    }
  }, [subBrandAccess]);

  useEffect(() => {
    const callFn = async () => {
      const documentData = JSON.parse(localStorage.getItem('manualDoumentFolder')) || {};
      let folderId = null;
      if (documentData?._id) {
        folderId = documentData?._id;
        await dispatch(action.getBreadCrumbDocumentData(documentData?._id));
      }
      const localstorageFilter = JSON.parse(localStorage.getItem('documentsFilter'));
      let reqObj = {};
      if (
        localstorageFilter &&
        (localstorageFilter?.brand?.length ||
          localstorageFilter?.suppliers?.length ||
          localstorageFilter?.documentFilterType ||
          localstorageFilter?.subBrand?.length ||
          localstorageFilter?.startDate ||
          localstorageFilter?.endDate)
      ) {
        apiCallWithFilter(localstorageFilter, folderId);
      } else {
        reqObj = {
          pageIndex: 1,
          pageSize: state.pageSize,
          folderId,
          isArchived: state?.isArchived,
        };

        setState((prev) => ({
          ...prev,
          loading: true,
        }));
        const res = await dispatch(action.getManualDocumentList(reqObj));
        if (res) {
          setState((prev) => ({
            ...prev,
            loading: false,
            pageIndex: 1,
          }));
        }
      }
    };

    callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const callApi = async () => {
      await dispatch(
        systemMasterAction.getBrandDetails({
          loggedInUserId: userInfo?.userId,
          isActive: 1,
        }),
      );
      await dispatch(
        systemMasterAction.fetchManualDocumentType({
          isActive: 1,
        }),
      );
      await dispatch(bomLibAction.fetchBomReferenceSupplier());
      await dispatch(systemMasterAction.getSubBrandAccess());
    };

    callApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set isSubBrandAccess based on subBrandAccess from Redux
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      isSubBrandAccess: subBrandAccess || false,
    }));
  }, [subBrandAccess]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      let res;
      if (state.searchLoaded) {
        const documentData = JSON.parse(localStorage.getItem('manualDoumentFolder')) || {};
        let folderId = null;
        if (documentData?._id) {
          folderId = documentData?._id;
          await dispatch(action.getBreadCrumbDocumentData(documentData?._id));
        }

        setState((debouncestate) => ({
          ...debouncestate,
          pageIndex: 1,
          hasMore: true,
          documentList: [],
          loading: true,
        }));
        dispatch(action.clearReducer());
        const reqObj = {
          pageIndex: state?.pageIndex,
          pageSize: state?.pageSize,
          searchText: state?.searchText,
          isArchived: state?.isArchived,
          folderId,
        };
        res = await dispatch(action.getManualDocumentList(reqObj));
      }
      if (res) {
        setState((debstate) => ({
          ...debstate,
          searchLoaded: false,
          loading: false,
        }));
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchText, dispatch]);

  const handleCheckAll = useCallback(
    (e) => {
      if (e.target.checked) {
        const allIds = state?.documentList && checkArray(state?.documentList) && state?.documentList?.map((ele) => ele._id);
        if (allIds) {
          setState((prevState) => ({
            ...prevState,
            selectedIds: allIds.filter((element) => element !== undefined),
          }));
        }
      } else {
        setState((prevState) => ({
          ...prevState,
          selectedIds: [],
        }));
      }
    },
    [state?.documentList, setState],
  );

  const contextValue = useMemo(() => {
    return {
      state,
      setState,
      userInfo,
      isallChecked,
      handleCheckAll,
      apiCallWithFilter,
      isSupplierLoggedIn,
      manualAddEditAccess,
      manualDeleteAccess,
    };
  }, [state, setState, userInfo, isallChecked, handleCheckAll, apiCallWithFilter, isSupplierLoggedIn, manualAddEditAccess, manualDeleteAccess]);

  return <StateContext.Provider value={contextValue}>{children}</StateContext.Provider>;
}

export function useManualState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the DAMModule State Provider');
  }
  return context;
}
